import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
// Tabs removed; sections are rendered per submodule via initialTab
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { 
  Calculator, Building, Wrench, FileSpreadsheet, Plus, Edit, Trash2, 
  Plane, Train, Pickaxe, Fuel, Hospital, School, Factory, 
  Database, Globe, Shield, TestTube, AlertTriangle, Crown,
  Link, ExternalLink, RefreshCw, Settings, BarChart3, Save
} from "lucide-react";
import { 
  CostingModel, CostComponent, CostSubcomponent, BillItem, 
  BESMM4Code, GeoZone, RiskProfile, MaterialPrice, CostModel 
} from "../../types";
import { toast } from "sonner";

interface CostModelProps {
  initialTab?: "overview" | "components" | "subcomponents" | "geozones" | "models";
}

const CostModelModule = ({ initialTab = "overview" }: CostModelProps) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  // Keep activeTab in sync with route-driven initialTab
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  const [selectedComponent, setSelectedComponent] = useState<string>("all");
  const [selectedGeoZone, setSelectedGeoZone] = useState<string>("all");
  const [isAddComponentDialogOpen, setIsAddComponentDialogOpen] = useState(false);
  const [isEditComponentDialogOpen, setIsEditComponentDialogOpen] = useState(false);
  const [isAddSubcomponentDialogOpen, setIsAddSubcomponentDialogOpen] = useState(false);
  const [isEditSubcomponentDialogOpen, setIsEditSubcomponentDialogOpen] = useState(false);
  const [editingComponent, setEditingComponent] = useState<CostComponent | null>(null);
  const [editingSubcomponent, setEditingSubcomponent] = useState<CostSubcomponent | null>(null);
  const [selectedModel, setSelectedModel] = useState<CostingModel | null>(null);
  const [calculationResults, setCalculationResults] = useState<any>(null);
  const [filterType, setFilterType] = useState<string>("all");
  
  // Sample data for the comprehensive cost model system
  const [costComponents, setCostComponents] = useState<CostComponent[]>([
    { id: "1", name: "Material Cost", description: "Cost of materials delivered to site", orderIndex: 1, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "2", name: "Labour Cost", description: "Cost of labour required to execute the work", orderIndex: 2, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "3", name: "Plant and Equipment Cost", description: "Cost of using plant and equipment", orderIndex: 3, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "4", name: "Transportation Cost", description: "Cost of transportation and logistics", orderIndex: 4, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "5", name: "Overheads and Profit", description: "Site and head office overheads plus profit margin", orderIndex: 5, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "6", name: "Insurance", description: "Various insurance coverages required", orderIndex: 6, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "7", name: "Testing and Quality Control", description: "Material testing and quality assurance", orderIndex: 7, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "8", name: "Contingencies and Risk Allowance", description: "Risk factors and contingencies", orderIndex: 8, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "9", name: "Royalties and Levies", description: "Government levies and statutory fees", orderIndex: 9, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
  ]);

  const [subcomponents, setSubcomponents] = useState<CostSubcomponent[]>([
    // Material Cost Subcomponents
    { id: "1", costComponentId: "1", name: "Base cost of materials (ex-works)", description: "Raw material cost from supplier", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "2", costComponentId: "1", name: "Packaging and handling charges", description: "Cost of packaging and handling materials", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "3", costComponentId: "1", name: "Transportation (supplier to site)", description: "Transportation cost from supplier to construction site", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "4", costComponentId: "1", name: "Unloading and stacking on site", description: "Cost of unloading and stacking materials on site", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "5", costComponentId: "1", name: "Import duties and taxes", description: "Import duties and taxes for imported materials", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "6", costComponentId: "1", name: "Waste and losses allowance", description: "Allowance for material waste and losses during construction", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Labour Cost Subcomponents
    { id: "7", costComponentId: "2", name: "Skilled labour wages", description: "Wages for skilled workers (masons, carpenters, etc.)", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "8", costComponentId: "2", name: "Semi-skilled labour wages", description: "Wages for semi-skilled workers", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "9", costComponentId: "2", name: "Unskilled labour wages", description: "Wages for unskilled workers", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "10", costComponentId: "2", name: "Overtime and bonus", description: "Overtime pay and performance bonuses", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "11", costComponentId: "2", name: "Labour accommodation", description: "Accommodation costs for workers", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "12", costComponentId: "2", name: "Transport to site", description: "Transportation costs for workers to site", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Plant and Equipment Subcomponents
    { id: "13", costComponentId: "3", name: "Owned plant depreciation", description: "Depreciation costs for owned equipment", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "14", costComponentId: "3", name: "Plant maintenance costs", description: "Maintenance and repair costs for equipment", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "15", costComponentId: "3", name: "Plant fuel costs", description: "Fuel costs for equipment operation", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "16", costComponentId: "3", name: "Hired plant rental charges", description: "Rental costs for hired equipment", defaultUnit: "NGN/day", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Overheads and Profit Subcomponents
    { id: "17", costComponentId: "5", name: "Site office costs", description: "Costs for site office and facilities", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "18", costComponentId: "5", name: "Site supervision costs", description: "Costs for site supervision and management", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "19", costComponentId: "5", name: "Head office overheads", description: "Head office overhead costs", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "20", costComponentId: "5", name: "Contractor's profit margin", description: "Contractor's profit margin", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Insurance Subcomponents
    { id: "21", costComponentId: "6", name: "Contractor's All Risk (CAR) Insurance", description: "Comprehensive insurance coverage", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "22", costComponentId: "6", name: "Third-party liability insurance", description: "Third-party liability coverage", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "23", costComponentId: "6", name: "Performance bond premiums", description: "Performance bond insurance premiums", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Testing and Quality Control Subcomponents
    { id: "24", costComponentId: "7", name: "Soil compaction tests", description: "Testing for soil compaction", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "25", costComponentId: "7", name: "Concrete cube tests", description: "Testing for concrete strength", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "26", costComponentId: "7", name: "Third-party certification fees", description: "Fees for third-party certification", defaultUnit: "NGN", calculationType: "fixed", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Contingencies and Risk Subcomponents
    { id: "27", costComponentId: "8", name: "Design risks allowance", description: "Allowance for design-related risks", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "28", costComponentId: "8", name: "Material price escalation", description: "Allowance for material price increases", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "29", costComponentId: "8", name: "Weather delays allowance", description: "Allowance for weather-related delays", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Royalties and Levies Subcomponents
    { id: "30", costComponentId: "9", name: "Value Added Tax (VAT)", description: "Value Added Tax on materials and services", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "31", costComponentId: "9", name: "Withholding Tax (WHT)", description: "Withholding Tax deductions", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "32", costComponentId: "9", name: "State infrastructure levies", description: "State government infrastructure levies", defaultUnit: "%", calculationType: "percentage", shared: true, isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
  ]);

  // Form state for components
  const [componentFormData, setComponentFormData] = useState({
    name: "",
    description: "",
    orderIndex: 1,
    isActive: true,
  });

  // Form state for subcomponents
  const [subcomponentFormData, setSubcomponentFormData] = useState<{
    name: string;
    description: string;
    costComponentId: string;
    defaultUnit: string;
    calculationType: 'fixed' | 'percentage' | 'formula' | 'material_library';
    shared: boolean;
    isActive: boolean;
  }>({
    name: "",
    description: "",
    costComponentId: "",
    defaultUnit: "",
    calculationType: "fixed",
    shared: true,
    isActive: true,
  });

  const [geoZones] = useState<GeoZone[]>([
    { id: "1", name: "Lagos", code: "LAG", adjustmentFactor: 1.15, description: "Lagos State - High cost area", isActive: true, transportHub: true, industrialCenter: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "2", name: "Abuja", code: "ABJ", adjustmentFactor: 1.12, description: "Federal Capital Territory", isActive: true, transportHub: true, industrialCenter: false, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "3", name: "Port Harcourt", code: "PHC", adjustmentFactor: 1.10, description: "Rivers State", isActive: true, transportHub: true, industrialCenter: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "4", name: "Kano", code: "KAN", adjustmentFactor: 1.05, description: "Kano State", isActive: true, transportHub: false, industrialCenter: false, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "5", name: "Enugu", code: "ENU", adjustmentFactor: 1.02, description: "Enugu State", isActive: true, transportHub: false, industrialCenter: false, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
  ]);

  const [riskProfiles] = useState<RiskProfile[]>([
    { id: "1", name: "Low Risk", riskFactor: 0.02, category: "Low", description: "Standard projects with minimal risk", isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "2", name: "Medium Risk", riskFactor: 0.05, category: "Medium", description: "Projects with moderate complexity", isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "3", name: "High Risk", riskFactor: 0.10, category: "High", description: "Complex projects with significant risk", isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "4", name: "Very High Risk", riskFactor: 0.15, category: "Very High", description: "Highly complex or emergency projects", isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
  ]);

  const [besmm4Codes] = useState<BESMM4Code[]>([
    // Section 1: Building Works with Subcodes
    // 1. PRELIMINARIES
    { id: "1", code: "1", description: "PRELIMINARIES", category: "General", section: "1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "2", code: "1A", description: "Information and Requirements", category: "Preliminaries", section: "1A", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "3", code: "1A1", description: "Project Particulars (name, nature, location, contract length, parties)", category: "Project Management", section: "1A1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "4", code: "1A2", description: "Drawings & Documents (list, revisions, access)", category: "Documentation", section: "1A2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "5", code: "1A3", description: "Site/Existing Buildings (description, constraints, hazards)", category: "Site Conditions", section: "1A3", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "6", code: "1A4", description: "Description of Works (scope, preparatory/concurrent works)", category: "Scope Definition", section: "1A4", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "7", code: "1A5", description: "Contract Conditions (form of contract, amendments)", category: "Contract", section: "1A5", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "8", code: "1A6", description: "Document Interpretation (definitions, standards, substitutions)", category: "Documentation", section: "1A6", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "9", code: "1A7", description: "Management of Works (supervision, programming, reporting)", category: "Project Management", section: "1A7", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "10", code: "1A8", description: "Quality Standards (workmanship, samples, inspections)", category: "Quality Control", section: "1A8", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "11", code: "1A9", description: "Security/Safety (hazards, protection, fire prevention)", category: "HSE", section: "1A9", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "12", code: "1A10", description: "Method Limitations (working hours, sequencing, phasing)", category: "Construction Methods", section: "1A10", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "13", code: "1A11", description: "Site Accommodation/Facilities (offices, services, IT)", category: "Site Establishment", section: "1A11", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "14", code: "1A12", description: "Operation/Maintenance (manuals, training, spare parts)", category: "Handover", section: "1A12", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "15", code: "1B", description: "Pricing Schedule", category: "Cost Management", section: "1B", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "16", code: "1B1", description: "Employer's Requirements (accommodation, records, O&M services)", category: "Employer Costs", section: "1B1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "17", code: "1B2", description: "Contractor's Cost Items (management, site establishment, services)", category: "Contractor Costs", section: "1B2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2. Off-Site Manufactured Materials, Components or Buildings
    { id: "18", code: "2", description: "Off-Site Manufactured Materials, Components or Buildings", category: "Off-Site", section: "2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 3. DEMOLITION
    { id: "19", code: "3", description: "DEMOLITION", category: "Demolition", section: "3", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "20", code: "3.1", description: "Structural Demolition (buildings, bridges, chimneys)", category: "Structural", section: "3.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "21", code: "3.2", description: "Non-Structural Removal (partitions, services, pavings)", category: "Non-Structural", section: "3.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "22", code: "3.3", description: "Hazardous Material Removal (asbestos, chemicals)", category: "Hazardous", section: "3.3", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 4. Alterations, Repairs and Conservation
    { id: "23", code: "4", description: "Alterations, Repairs and Conservation", category: "Alterations", section: "4", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 5. EXCAVATING AND FILLING
    { id: "24", code: "5", description: "EXCAVATING AND FILLING", category: "Earthworks", section: "5", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "25", code: "5.1", description: "Bulk Excavation (topsoil, rock, maximum depth)", category: "Bulk Operations", section: "5.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "26", code: "5.2", description: "Trenches (pipes, cables, width/depth classification)", category: "Linear Excavation", section: "5.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "27", code: "5.3", description: "Filling (backfill, compaction, material types)", category: "Backfilling", section: "5.3", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 6. Ground Remediation and Soil Stabilisation
    { id: "28", code: "6", description: "Ground Remediation and Soil Stabilisation", category: "Groundwork", section: "6", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 7. Piling
    { id: "29", code: "7", description: "Piling", category: "Foundations", section: "7", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 8. Underpinning
    { id: "30", code: "8", description: "Underpinning", category: "Foundations", section: "8", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 9. Diaphragm Walls and Embedded Retaining Walls
    { id: "31", code: "9", description: "Diaphragm Walls and Embedded Retaining Walls", category: "Structures", section: "9", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 10. Crib Walls, Gabions and Reinforced Earth
    { id: "32", code: "10", description: "Crib Walls, Gabions and Reinforced Earth", category: "Structures", section: "10", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 11. INSITU CONCRETE WORKS
    { id: "33", code: "11", description: "INSITU CONCRETE WORKS", category: "Concrete", section: "11", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "34", code: "11.1", description: "Foundations (pads, strips, rafts)", category: "Foundations", section: "11.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "35", code: "11.2", description: "Structural Elements (columns, beams, slabs)", category: "Structural", section: "11.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "36", code: "11.3", description: "Formwork (faces, curves, special shapes)", category: "Formwork", section: "11.3", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 12. Precast / Composite Concrete
    { id: "37", code: "12", description: "Precast / Composite Concrete", category: "Concrete", section: "12", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 13. Precast Concrete
    { id: "38", code: "13", description: "Precast Concrete", category: "Concrete", section: "13", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 14. MASONRY
    { id: "39", code: "14", description: "MASONRY", category: "Masonry", section: "14", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "40", code: "14.1", description: "Walls (brick, block, stone - loadbearing/non-loadbearing)", category: "Walls", section: "14.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "41", code: "14.2", description: "Special Bonds (raking, curved, reinforced)", category: "Special Construction", section: "14.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 15. Structural Metalwork
    { id: "42", code: "15", description: "Structural Metalwork", category: "Steelwork", section: "15", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 16. Carpentry
    { id: "43", code: "16", description: "Carpentry", category: "Carpentry", section: "16", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 17. Sheet Roof Coverings
    { id: "44", code: "17", description: "Sheet Roof Coverings", category: "Roofing", section: "17", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 18. Tile and Slate Roof and Wall Coverings
    { id: "45", code: "18", description: "Tile and Slate Roof and Wall Coverings", category: "Roofing", section: "18", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 19. WATERPROOFING
    { id: "46", code: "19", description: "WATERPROOFING", category: "Waterproofing", section: "19", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "47", code: "19.1", description: "Basements/Tanks (liquid applied, sheet membranes)", category: "Below-Grade", section: "19.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "48", code: "19.2", description: "Roofs/Decks (bituminous, polymeric)", category: "Above-Grade", section: "19.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 20. Proprietary Linings and Partitions
    { id: "49", code: "20", description: "Proprietary Linings and Partitions", category: "Partitions", section: "20", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 21. Cladding and Covering
    { id: "50", code: "21", description: "Cladding and Covering", category: "Cladding", section: "21", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 22. General Joinery
    { id: "51", code: "22", description: "General Joinery", category: "Joinery", section: "22", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 23. Windows, Screens and Lights
    { id: "52", code: "23", description: "Windows, Screens and Lights", category: "Windows", section: "23", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 24. Doors, Shutters and Hatches
    { id: "53", code: "24", description: "Doors, Shutters and Hatches", category: "Doors", section: "24", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 25. Stairs, Walkways and Balustrades
    { id: "54", code: "25", description: "Stairs, Walkways and Balustrades", category: "Structures", section: "25", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 26. Metalwork
    { id: "55", code: "26", description: "Metalwork", category: "Metalwork", section: "26", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 27. Glazing
    { id: "56", code: "27", description: "Glazing", category: "Glazing", section: "27", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 28. FLOOR, WALL, CEILING AND ROOF FINISHES
    { id: "57", code: "28", description: "FLOOR, WALL, CEILING AND ROOF FINISHES", category: "Finishes", section: "28", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "58", code: "28.1", description: "Floor Finishes (screeds, tiles, wood, resilient)", category: "Flooring", section: "28.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "59", code: "28.2", description: "Wall Finishes (plaster, tiling, cladding)", category: "Wall Finishes", section: "28.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "60", code: "28.3", description: "Ceiling Finishes (plaster, boards)", category: "Ceiling Finishes", section: "28.3", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 29. Decoration
    { id: "61", code: "29", description: "Decoration", category: "Finishes", section: "29", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 30. Suspended Ceiling
    { id: "62", code: "30", description: "Suspended Ceiling", category: "Ceilings", section: "30", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 31. Insulation, Fire Stopping and Fire Protection
    { id: "63", code: "31", description: "Insulation, Fire Stopping and Fire Protection", category: "Insulation", section: "31", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 32. Furniture, Fittings and Equipment
    { id: "64", code: "32", description: "Furniture, Fittings and Equipment", category: "FF&E", section: "32", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 33. Drainage Above Ground
    { id: "65", code: "33", description: "Drainage Above Ground", category: "Drainage", section: "33", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 34. Drainage Below Ground
    { id: "66", code: "34", description: "Drainage Below Ground", category: "Drainage", section: "34", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 35. Site Works
    { id: "67", code: "35", description: "Site Works", category: "Sitework", section: "35", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 36. Fencing
    { id: "68", code: "36", description: "Fencing", category: "Sitework", section: "36", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 37. Soft Landscaping
    { id: "69", code: "37", description: "Soft Landscaping", category: "Landscaping", section: "37", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 38. MECHANICAL SERVICES
    { id: "70", code: "38", description: "MECHANICAL SERVICES", category: "Services", section: "38", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "71", code: "38.1", description: "HVAC (ductwork, equipment, controls)", category: "HVAC", section: "38.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "72", code: "38.2", description: "Plumbing (pipes, fixtures, tanks)", category: "Plumbing", section: "38.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 39. ELECTRICAL SERVICES
    { id: "73", code: "39", description: "ELECTRICAL SERVICES", category: "Services", section: "39", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "74", code: "39.1", description: "Power Distribution (cables, switchgear)", category: "Power", section: "39.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "75", code: "39.2", description: "Lighting (luminaires, controls)", category: "Lighting", section: "39.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 40. Transportation
    { id: "76", code: "40", description: "Transportation", category: "Logistics", section: "40", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    
    // Section 2: Civil/Industrial Engineering Works with Subcodes
    // 2A. General Items
    { id: "77", code: "2A", description: "General Items", category: "General", section: "2A", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2B. Ground Investigation
    { id: "78", code: "2B", description: "Ground Investigation", category: "Sitework", section: "2B", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2C. Geotechnical and Other Specialist Processes
    { id: "79", code: "2C", description: "Geotechnical and Other Specialist Processes", category: "Groundwork", section: "2C", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2D. Demolition and Site Clearance
    { id: "80", code: "2D", description: "Demolition and Site Clearance", category: "Demolition", section: "2D", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2E. EARTHWORKS
    { id: "81", code: "2E", description: "EARTHWORKS", category: "Earthworks", section: "2E", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "82", code: "2E.1", description: "Bulk Earthmoving (cut/fill, grading)", category: "Bulk Operations", section: "2E.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "83", code: "2E.2", description: "Compaction (embankments, subgrades)", category: "Soil Improvement", section: "2E.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2F. INSITU CONCRETE
    { id: "84", code: "2F", description: "INSITU CONCRETE", category: "Concrete", section: "2F", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "85", code: "2F.1", description: "Mass Concrete (dams, culverts)", category: "Mass Structures", section: "2F.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "86", code: "2F.2", description: "Reinforced Concrete (bridges, retaining walls)", category: "Structural", section: "2F.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2G. Concrete Ancillaries
    { id: "87", code: "2G", description: "Concrete Ancillaries", category: "Concrete", section: "2G", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2H. Precast Concrete
    { id: "88", code: "2H", description: "Precast Concrete", category: "Concrete", section: "2H", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2J. PIPEWORK - PIPES
    { id: "89", code: "2J", description: "PIPEWORK - PIPES", category: "Piping", section: "2J", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "90", code: "2J.1", description: "Pipe Installation (buried, above-ground, materials)", category: "Installation", section: "2J.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "91", code: "2J.2", description: "Pressure Testing (hydrostatic, pneumatic)", category: "Testing", section: "2J.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2K. Pipework – Fittings and Valves
    { id: "92", code: "2K", description: "Pipework – Fittings and Valves", category: "Piping", section: "2K", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2L. Pipework – Manholes and Pipework Ancillaries
    { id: "93", code: "2L", description: "Pipework – Manholes and Pipework Ancillaries", category: "Piping", section: "2L", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2M. Pipework – Supports and Protection, Ancillaries to Laying Excavation
    { id: "94", code: "2M", description: "Pipework – Supports and Protection, Ancillaries to Laying Excavation", category: "Piping", section: "2M", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2N. Structural Metalwork
    { id: "95", code: "2N", description: "Structural Metalwork", category: "Steelwork", section: "2N", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2P. Miscellaneous Metalwork
    { id: "96", code: "2P", description: "Miscellaneous Metalwork", category: "Metalwork", section: "2P", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2Q. Timber
    { id: "97", code: "2Q", description: "Timber", category: "Timber", section: "2Q", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2R. Piles
    { id: "98", code: "2R", description: "Piles", category: "Foundations", section: "2R", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2S. Piling Ancillaries
    { id: "99", code: "2S", description: "Piling Ancillaries", category: "Foundations", section: "2S", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2T. ROADS AND PAVING
    { id: "100", code: "2T", description: "ROADS AND PAVING", category: "Paving", section: "2T", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "101", code: "2T.1", description: "Flexible Pavements (asphalt, bituminous)", category: "Flexible", section: "2T.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "102", code: "2T.2", description: "Rigid Pavements (concrete slabs, joints)", category: "Rigid", section: "2T.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2U. Railway Track Construction
    { id: "103", code: "2U", description: "Railway Track Construction", category: "Railway", section: "2U", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2V. TUNNEL
    { id: "104", code: "2V", description: "TUNNEL", category: "Tunneling", section: "2V", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "105", code: "2V.1", description: "Tunnel Excavation (drill-blast, TBM)", category: "Excavation", section: "2V.1", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "106", code: "2V.2", description: "Tunnel Lining (shotcrete, segmental)", category: "Lining", section: "2V.2", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2W. Brickwork, Blockwork and Masonry
    { id: "107", code: "2W", description: "Brickwork, Blockwork and Masonry", category: "Masonry", section: "2W", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2X. Painting
    { id: "108", code: "2X", description: "Painting", category: "Finishes", section: "2X", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2Y. Waterproofing
    { id: "109", code: "2Y", description: "Waterproofing", category: "Waterproofing", section: "2Y", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2Z. Miscellaneous Work
    { id: "110", code: "2Z", description: "Miscellaneous Work", category: "General", section: "2Z", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2AA. Sewer and Water Main Renovation and Ancillary Works
    { id: "111", code: "2AA", description: "Sewer and Water Main Renovation and Ancillary Works", category: "Utilities", section: "2AA", createdAt: "2024-01-01", updatedAt: "2024-01-01" },

    // 2AB. Simple Building Works Incidental to Civil Engineering Works
    { id: "112", code: "2AB", description: "Simple Building Works Incidental to Civil Engineering Works", category: "General", section: "2AB", createdAt: "2024-01-01", updatedAt: "2024-01-01" },
  ]);

  const [billItems] = useState<BillItem[]>([
    { id: "1", besmm4CodeId: "4", name: "Reinforced Concrete Foundations", description: "Grade 30 concrete foundations", unitOfMeasure: "m³", category: "Concrete", isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "2", besmm4CodeId: "3", name: "Excavation for Foundations", description: "Excavation to formation level", unitOfMeasure: "m³", category: "Earthworks", isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
    { id: "3", besmm4CodeId: "6", name: "Structural Steel Frame", description: "Steel columns and beams", unitOfMeasure: "tonne", category: "Steelwork", isActive: true, createdAt: "2024-01-01", updatedAt: "2024-01-01" },
  ]);

  // Legacy costing models for backward compatibility
  const [costingModels, setCostingModels] = useState<CostingModel[]>([
    {
      id: "CM-001",
      name: "Construction Cost per SQM",
      type: "construction",
      unitOfMeasure: "m²",
      formula: "(Material + Labour + Equipment) × Regional Factor × Complexity Factor",
      parameters: [
        { name: "Base Material Cost", value: 12000, unit: "NGN/m²", description: "Standard construction materials" },
        { name: "Labour Rate", value: 8000, unit: "NGN/m²", description: "Skilled and unskilled labour" },
        { name: "Equipment Cost", value: 3000, unit: "NGN/m²", description: "Machinery and tools" },
        { name: "Regional Factor", value: 1.2, unit: "multiplier", description: "Lagos regional adjustment" }
      ],
      applicableCategories: ["Building Construction", "Infrastructure"],
      region: "Lagos",
      isActive: true
    },
    {
      id: "CM-002", 
      name: "Road Construction per KM",
      type: "construction",
      unitOfMeasure: "km",
      formula: "Base Cost × Width Factor × Surface Type × Terrain Factor",
      parameters: [
        { name: "Base Cost", value: 45000000, unit: "NGN/km", description: "Standard asphalt road" },
        { name: "Width Factor", value: 1.0, unit: "multiplier", description: "Standard 7m width" },
        { name: "Surface Type", value: 1.2, unit: "multiplier", description: "Asphalt premium" },
        { name: "Terrain Factor", value: 1.1, unit: "multiplier", description: "Moderate terrain" }
      ],
      applicableCategories: ["Road Construction", "Infrastructure"],
      region: "National",
      isActive: true
    }
  ]);

  const calculateCost = (model: CostingModel, quantity: number = 1) => {
    let baseCost = 0;
    let factors = 1;

    model.parameters.forEach(param => {
      if (param.name.toLowerCase().includes('cost') || param.name.toLowerCase().includes('rate') || 
          param.name.toLowerCase().includes('work') || param.name.toLowerCase().includes('system') ||
          param.name.toLowerCase().includes('equipment') || param.name.toLowerCase().includes('building') ||
          param.name.toLowerCase().includes('unit') || param.name.toLowerCase().includes('structure') ||
          param.name.toLowerCase().includes('finishes') || param.name.toLowerCase().includes('services')) {
        baseCost += param.value;
      } else if (param.name.toLowerCase().includes('factor')) {
        factors *= param.value;
      }
    });

    const totalCost = baseCost * factors * quantity;
    return {
      baseCost,
      factors,
      totalCost,
      costPerUnit: totalCost / quantity
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getModelIcon = (type: string, name: string) => {
    if (name.toLowerCase().includes('hospital')) return Hospital;
    if (name.toLowerCase().includes('school')) return School;
    if (name.toLowerCase().includes('airport')) return Plane;
    if (name.toLowerCase().includes('railway') || name.toLowerCase().includes('rail')) return Train;
    if (name.toLowerCase().includes('mining')) return Pickaxe;
    if (name.toLowerCase().includes('oil') || name.toLowerCase().includes('gas')) return Fuel;
    if (name.toLowerCase().includes('industrial') || name.toLowerCase().includes('warehouse')) return Factory;
    
    switch (type) {
      case 'construction': return Building;
      case 'procurement': return FileSpreadsheet;
      case 'services': return Wrench;
      default: return Calculator;
    }
  };

  const getComponentIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case 'material cost': return Building;
      case 'labour cost': return Wrench;
      case 'plant and equipment cost': return Settings;
      case 'transportation cost': return Plane;
      case 'overheads and profit': return BarChart3;
      case 'insurance': return Shield;
      case 'testing and quality control': return TestTube;
      case 'contingencies and risk allowance': return AlertTriangle;
      case 'royalties and levies': return Crown;
      default: return Calculator;
    }
  };

  const getCalculationTypeColor = (type: string) => {
    switch (type) {
      case 'fixed': return 'bg-blue-100 text-blue-800';
      case 'percentage': return 'bg-green-100 text-green-800';
      case 'formula': return 'bg-purple-100 text-purple-800';
      case 'material_library': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSubcomponents = subcomponents.filter(sub => {
    if (selectedComponent === "all") return true;
    return sub.costComponentId === selectedComponent;
  });

  const filteredModels = costingModels.filter(model => {
    if (filterType === "all") return model.isActive;
    return model.isActive && model.type === filterType;
  });

  // Edit functions for components
  const handleEditComponent = (component: CostComponent) => {
    setEditingComponent(component);
    setComponentFormData({
      name: component.name,
      description: component.description,
      orderIndex: component.orderIndex,
      isActive: component.isActive,
    });
    setIsEditComponentDialogOpen(true);
  };

  const handleSaveComponent = () => {
    if (editingComponent) {
      // Update existing component
      setCostComponents(prev => prev.map(comp => 
        comp.id === editingComponent.id 
          ? { ...comp, ...componentFormData, updatedAt: new Date().toISOString() }
          : comp
      ));
      toast.success("Cost component updated successfully!");
    } else {
      // Add new component
      const newComponent: CostComponent = {
        id: Date.now().toString(),
        ...componentFormData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setCostComponents(prev => [...prev, newComponent]);
      toast.success("Cost component added successfully!");
    }
    
    // Reset form
    setComponentFormData({
      name: "",
      description: "",
      orderIndex: 1,
      isActive: true,
    });
    setEditingComponent(null);
    setIsEditComponentDialogOpen(false);
    setIsAddComponentDialogOpen(false);
  };

  const handleDeleteComponent = (id: string) => {
    setCostComponents(prev => prev.filter(comp => comp.id !== id));
    toast.success("Cost component deleted successfully!");
  };

  // Edit functions for subcomponents
  const handleEditSubcomponent = (subcomponent: CostSubcomponent) => {
    setEditingSubcomponent(subcomponent);
    setSubcomponentFormData({
      name: subcomponent.name,
      description: subcomponent.description || "",
      costComponentId: subcomponent.costComponentId,
      defaultUnit: subcomponent.defaultUnit,
      calculationType: subcomponent.calculationType as 'fixed' | 'percentage' | 'formula' | 'material_library',
      shared: subcomponent.shared,
      isActive: subcomponent.isActive,
    });
    setIsEditSubcomponentDialogOpen(true);
  };

  const handleSaveSubcomponent = () => {
    if (editingSubcomponent) {
      // Update existing subcomponent
      setSubcomponents(prev => prev.map(sub => 
        sub.id === editingSubcomponent.id 
          ? { ...sub, ...subcomponentFormData, updatedAt: new Date().toISOString() }
          : sub
      ));
      toast.success("Cost subcomponent updated successfully!");
    } else {
      // Add new subcomponent
      const newSubcomponent: CostSubcomponent = {
        id: Date.now().toString(),
        ...subcomponentFormData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      setSubcomponents(prev => [...prev, newSubcomponent]);
      toast.success("Cost subcomponent added successfully!");
    }
    
    // Reset form
    setSubcomponentFormData({
      name: "",
      description: "",
      costComponentId: "",
      defaultUnit: "",
      calculationType: "fixed",
      shared: true,
      isActive: true,
    });
    setEditingSubcomponent(null);
    setIsEditSubcomponentDialogOpen(false);
    setIsAddSubcomponentDialogOpen(false);
  };

  const handleDeleteSubcomponent = (id: string) => {
    setSubcomponents(prev => prev.filter(sub => sub.id !== id));
    toast.success("Cost subcomponent deleted successfully!");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Cost Model Module</h2>
          <p className="text-gray-600">BESMM4-compliant cost modeling with dynamic pricing and geo-adjustments</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync Prices
          </Button>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create Model
          </Button>
        </div>
      </div>

      {/* System Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Components</CardTitle>
            <Database className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{costComponents.length}</div>
            <p className="text-xs text-muted-foreground">Main cost categories</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Subcomponents</CardTitle>
            <Link className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{subcomponents.length}</div>
            <p className="text-xs text-muted-foreground">Detailed cost items</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Geo Zones</CardTitle>
            <Globe className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{geoZones.length}</div>
            <p className="text-xs text-muted-foreground">Regional adjustments</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">BESMM4 Codes</CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{besmm4Codes.length}</div>
            <p className="text-xs text-muted-foreground">Standard references</p>
          </CardContent>
        </Card>
      </div>

      {/* Section: System Overview */}
      {activeTab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Cost Components Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Database className="h-5 w-5 mr-2" />
                  Cost Components Structure
                </CardTitle>
                <CardDescription>9 main cost categories with hierarchical subcomponents</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {costComponents.map((component) => {
                    const Icon = getComponentIcon(component.name);
                    const componentSubs = subcomponents.filter(sub => sub.costComponentId === component.id);
                    return (
                      <div key={component.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center">
                          <Icon className="h-4 w-4 mr-2 text-gray-600" />
                          <div>
                            <p className="font-medium text-sm">{component.name}</p>
                            <p className="text-xs text-gray-500">{componentSubs.length} subcomponents</p>
                          </div>
                        </div>
                        <Badge variant="outline">{component.orderIndex}</Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* BESMM4 Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileSpreadsheet className="h-5 w-5 mr-2" />
                  BESMM4 Integration
                </CardTitle>
                <CardDescription>Standard bill items linked to BESMM4 codes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {billItems.map((item) => {
                    const besmm4 = besmm4Codes.find(code => code.id === item.besmm4CodeId);
                    return (
                      <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <p className="text-xs text-gray-500">
                            {besmm4?.code} • {item.unitOfMeasure} • {item.category}
                          </p>
                        </div>
                        <Badge variant="outline">{item.unitOfMeasure}</Badge>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Key Features */}
          <Card>
            <CardHeader>
              <CardTitle>Key System Features</CardTitle>
              <CardDescription>Advanced capabilities for comprehensive cost modeling</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center mb-2">
                    <Link className="h-5 w-5 mr-2 text-blue-600" />
                    <h4 className="font-medium">Reusable Subcomponents</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Shared components like VAT propagate changes across all cost models automatically
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center mb-2">
                    <ExternalLink className="h-5 w-5 mr-2 text-green-600" />
                    <h4 className="font-medium">Dynamic Pricing</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Real-time price updates from APIs (Dangote, FOCI) and material price libraries
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center mb-2">
                    <Globe className="h-5 w-5 mr-2 text-purple-600" />
                    <h4 className="font-medium">Geo-Adjustments</h4>
                  </div>
                  <p className="text-sm text-gray-600">
                    Regional cost factors applied automatically based on project location
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Section: Cost Components */}
      {activeTab === "components" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-semibold">Cost Components</h4>
              <p className="text-gray-600">Main cost categories with hierarchical subcomponents</p>
            </div>
            <Button onClick={() => setIsAddComponentDialogOpen(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Component
            </Button>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {costComponents.map((component) => {
              const Icon = getComponentIcon(component.name);
              const componentSubs = subcomponents.filter(sub => sub.costComponentId === component.id);
              const sharedCount = componentSubs.filter(sub => sub.shared).length;
              
              return (
                <Card key={component.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-2" />
                        <span className="text-sm">{component.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">#{component.orderIndex}</Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditComponent(component)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteComponent(component.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardTitle>
                    <CardDescription>{component.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <Label className="text-xs font-medium">Subcomponents</Label>
                        <p className="font-bold">{componentSubs.length}</p>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Shared Items</Label>
                        <p className="font-bold text-green-600">{sharedCount}</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-xs font-medium">Sample Subcomponents</Label>
                      <div className="space-y-1 mt-2 max-h-32 overflow-y-auto">
                        {componentSubs.slice(0, 3).map((sub) => (
                          <div key={sub.id} className="flex justify-between items-center p-2 bg-gray-50 rounded text-xs">
                            <span className="truncate">{sub.name}</span>
                            <div className="flex items-center space-x-1">
                              <Badge className={`text-xs ${getCalculationTypeColor(sub.calculationType)}`}>
                                {sub.calculationType}
                              </Badge>
                              {sub.shared && <Link className="h-3 w-3 text-green-600" />}
                            </div>
                          </div>
                        ))}
                        {componentSubs.length > 3 && (
                          <div className="text-xs text-gray-500 text-center">
                            +{componentSubs.length - 3} more subcomponents
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Section: Subcomponents */}
      {activeTab === "subcomponents" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-semibold">Cost Subcomponents</h4>
              <p className="text-gray-600">Detailed cost items with calculation types and sharing capabilities</p>
            </div>
            <div className="flex items-center gap-4">
              <Select value={selectedComponent} onValueChange={setSelectedComponent}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by component" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Components</SelectItem>
                  {costComponents.map((comp) => (
                    <SelectItem key={comp.id} value={comp.id}>
                      {comp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => setIsAddSubcomponentDialogOpen(true)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Subcomponent
              </Button>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subcomponent</TableHead>
                <TableHead>Component</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Shared</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSubcomponents.map((sub) => {
                const component = costComponents.find(comp => comp.id === sub.costComponentId);
                return (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{sub.name}</div>
                        <div className="text-sm text-gray-600">{sub.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{component?.name}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">{sub.defaultUnit}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={getCalculationTypeColor(sub.calculationType)}>
                        {sub.calculationType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {sub.shared ? (
                        <div className="flex items-center">
                          <Link className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-sm text-green-600">Shared</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">Local</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditSubcomponent(sub)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteSubcomponent(sub.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Section: Geo Zones */}
      {activeTab === "geozones" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {geoZones.map((zone) => (
              <Card key={zone.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Globe className="h-5 w-5 mr-2" />
                      <span>{zone.name}</span>
                    </div>
                    <Badge variant="outline">{zone.code}</Badge>
                  </CardTitle>
                  <CardDescription>{zone.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Adjustment Factor</span>
                        <span className="text-lg font-bold text-blue-700">
                          {zone.adjustmentFactor}x
                        </span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        {((zone.adjustmentFactor - 1) * 100).toFixed(0)}% cost adjustment
                      </p>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        <BarChart3 className="h-3 w-3 mr-1" />
                        Analytics
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Main module default: Cost Models (no tab header) */}
      {activeTab === "models" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredModels.map((model) => {
              const Icon = getModelIcon(model.type, model.name);
              const sampleCalc = calculateCost(model, model.unitOfMeasure === 'unit' ? 1 : 100);
              
              return (
                <Card key={model.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-2" />
                        <span className="text-sm">{model.name}</span>
                      </div>
                      <Badge variant="outline" className="capitalize">
                        {model.type}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="text-sm">{model.formula}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Unit of Measure</Label>
                        <p className="text-lg font-bold">{model.unitOfMeasure}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Region</Label>
                        <p className="text-lg font-bold">{model.region}</p>
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Key Parameters</Label>
                      <div className="space-y-1 mt-2 max-h-32 overflow-y-auto">
                        {model.parameters.slice(0, 3).map((param, index) => (
                          <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded text-xs">
                            <span className="truncate">{param.name}</span>
                            <span className="font-medium whitespace-nowrap ml-2">
                              {param.name.toLowerCase().includes('cost') || param.name.toLowerCase().includes('rate') || param.name.toLowerCase().includes('work') || param.name.toLowerCase().includes('system') || param.name.toLowerCase().includes('equipment') || param.name.toLowerCase().includes('building') || param.name.toLowerCase().includes('unit') || param.name.toLowerCase().includes('structure') || param.name.toLowerCase().includes('finishes') || param.name.toLowerCase().includes('services')
                                ? formatCurrency(param.value)
                                : `${param.value} ${param.unit}`}
                            </span>
                          </div>
                        ))}
                        {model.parameters.length > 3 && (
                          <div className="text-xs text-gray-500 text-center">
                            +{model.parameters.length - 3} more parameters
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-3 bg-blue-50 rounded-lg">
                      <h4 className="font-medium text-blue-700 mb-2 text-sm">
                        Sample ({model.unitOfMeasure === 'unit' ? '1' : '100'} {model.unitOfMeasure})
                      </h4>
                      <div className="text-sm space-y-1">
                        <div className="flex justify-between">
                          <span>Total Cost:</span>
                          <span className="font-bold text-blue-700">{formatCurrency(sampleCalc.totalCost)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" className="flex-1 text-xs">
                        <Calculator className="h-3 w-3 mr-1" />
                        Use Model
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Add/Edit Component Dialog */}
      <Dialog open={isAddComponentDialogOpen || isEditComponentDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsAddComponentDialogOpen(false);
          setIsEditComponentDialogOpen(false);
          setEditingComponent(null);
          setComponentFormData({
            name: "",
            description: "",
            orderIndex: 1,
            isActive: true,
          });
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingComponent ? "Edit Cost Component" : "Add Cost Component"}
            </DialogTitle>
            <DialogDescription>
              {editingComponent 
                ? "Update the cost component details below."
                : "Add a new cost component to the system."
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="componentName">Name</Label>
              <Input
                id="componentName"
                value={componentFormData.name}
                onChange={(e) => setComponentFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter component name"
              />
            </div>
            
            <div>
              <Label htmlFor="componentDescription">Description</Label>
              <Textarea
                id="componentDescription"
                value={componentFormData.description}
                onChange={(e) => setComponentFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description"
              />
            </div>

            <div>
              <Label htmlFor="componentOrderIndex">Order Index</Label>
              <Input
                id="componentOrderIndex"
                type="number"
                value={componentFormData.orderIndex}
                onChange={(e) => setComponentFormData(prev => ({ ...prev, orderIndex: parseInt(e.target.value) || 1 }))}
                placeholder="Enter order index"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="componentIsActive"
                checked={componentFormData.isActive}
                onCheckedChange={(checked) => setComponentFormData(prev => ({ ...prev, isActive: checked }))}
              />
              <Label htmlFor="componentIsActive">Active</Label>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => {
              setIsAddComponentDialogOpen(false);
              setIsEditComponentDialogOpen(false);
              setEditingComponent(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveComponent} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              {editingComponent ? "Update" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Subcomponent Dialog */}
      <Dialog open={isAddSubcomponentDialogOpen || isEditSubcomponentDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setIsAddSubcomponentDialogOpen(false);
          setIsEditSubcomponentDialogOpen(false);
          setEditingSubcomponent(null);
          setSubcomponentFormData({
            name: "",
            description: "",
            costComponentId: "",
            defaultUnit: "",
            calculationType: "fixed",
            shared: true,
            isActive: true,
          });
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingSubcomponent ? "Edit Cost Subcomponent" : "Add Cost Subcomponent"}
            </DialogTitle>
            <DialogDescription>
              {editingSubcomponent 
                ? "Update the cost subcomponent details below."
                : "Add a new cost subcomponent to the system."
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="subcomponentName">Name</Label>
              <Input
                id="subcomponentName"
                value={subcomponentFormData.name}
                onChange={(e) => setSubcomponentFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter subcomponent name"
              />
            </div>
            
            <div>
              <Label htmlFor="subcomponentDescription">Description</Label>
              <Textarea
                id="subcomponentDescription"
                value={subcomponentFormData.description}
                onChange={(e) => setSubcomponentFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter description"
              />
            </div>

            <div>
              <Label htmlFor="subcomponentCostComponentId">Cost Component</Label>
              <Select value={subcomponentFormData.costComponentId} onValueChange={(value) => setSubcomponentFormData(prev => ({ ...prev, costComponentId: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select cost component" />
                </SelectTrigger>
                <SelectContent>
                  {costComponents.map(component => (
                    <SelectItem key={component.id} value={component.id}>
                      {component.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="subcomponentDefaultUnit">Default Unit</Label>
                <Input
                  id="subcomponentDefaultUnit"
                  value={subcomponentFormData.defaultUnit}
                  onChange={(e) => setSubcomponentFormData(prev => ({ ...prev, defaultUnit: e.target.value }))}
                  placeholder="e.g., NGN, %, kg"
                />
              </div>
              
              <div>
                <Label htmlFor="subcomponentCalculationType">Calculation Type</Label>
                <Select value={subcomponentFormData.calculationType} onValueChange={(value: any) => setSubcomponentFormData(prev => ({ ...prev, calculationType: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="formula">Formula</SelectItem>
                    <SelectItem value="material_library">Material Library</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="subcomponentShared"
                  checked={subcomponentFormData.shared}
                  onCheckedChange={(checked) => setSubcomponentFormData(prev => ({ ...prev, shared: checked }))}
                />
                <Label htmlFor="subcomponentShared">Shared Component</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="subcomponentIsActive"
                  checked={subcomponentFormData.isActive}
                  onCheckedChange={(checked) => setSubcomponentFormData(prev => ({ ...prev, isActive: checked }))}
                />
                <Label htmlFor="subcomponentIsActive">Active</Label>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => {
              setIsAddSubcomponentDialogOpen(false);
              setIsEditSubcomponentDialogOpen(false);
              setEditingSubcomponent(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveSubcomponent} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              {editingSubcomponent ? "Update" : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CostModelModule; 